/**
 * HtmlBlock - Renders sanitized HTML content
 *
 * Follows the BrandMap HTML pattern for embedding custom content.
 * Uses DOMPurify to sanitize HTML and prevent XSS attacks.
 *
 * Iframes are allowed but restricted to known embed domains
 * (YouTube, Twitch, Kick, Vimeo, Streamlabs) to prevent
 * arbitrary third-party content injection.
 */

import DOMPurify from "dompurify";
import "./HtmlBlock.scss";

/** Allowlisted iframe embed domains. Exported so other sanitization sites (e.g. CustomSection) reuse the same list — keeps the domain allowlist in one place. */
export const ALLOWED_IFRAME_HOSTS = [
    "youtube.com",
    "www.youtube.com",
    "youtube-nocookie.com",
    "www.youtube-nocookie.com",
    "player.twitch.tv",
    "clips.twitch.tv",
    "twitch.tv",
    "www.twitch.tv",
    "player.kick.com",
    "kick.com",
    "player.vimeo.com",
    "streamlabs.com",
];

interface HtmlBlockProps {
    content?: string;
    widthPercentage?: number;
}

export const HtmlBlock = ({ content, widthPercentage = 100 }: HtmlBlockProps) => {
    if (!content) return null;

    // Sanitize HTML to prevent XSS
    const sanitizedHtml = DOMPurify.sanitize(content, {
        ADD_TAGS: ["iframe"],
        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "src"],
    });

    // Post-sanitization: strip iframes whose src doesn't match the domain allowlist
    const div = document.createElement("div");
    div.innerHTML = sanitizedHtml;
    div.querySelectorAll("iframe").forEach((iframe) => {
        try {
            const url = new URL(iframe.getAttribute("src") || "");
            const hostname = url.hostname.toLowerCase();
            if (!ALLOWED_IFRAME_HOSTS.some((h) => hostname === h || hostname.endsWith(`.${h}`))) {
                iframe.remove();
            }
        } catch {
            iframe.remove();
        }
    });
    const finalHtml = div.innerHTML;

    return (
        <div
            className="html-block"
            style={{ maxWidth: `${widthPercentage}%` }}
        >
            <div
                className="html-block__content"
                dangerouslySetInnerHTML={{ __html: finalHtml }}
            />
        </div>
    );
};
